/**
 * Pure clause-tree operations behind the egress filter builder (ADR-036 §D-4).
 *
 * React-free, so the tree edits are unit-testable without a DOM. A path is a
 * list of child indices from the root (``[]`` is the root itself), which is all
 * the addressing a small hand-authored predicate needs and keeps every edit a
 * pure structural transform rather than a mutation of shared state.
 *
 * # The one semantic that matters
 *
 * **There is deliberately no way to spell "allow everything."** The document is
 * fail-safe default-deny (REQ-PEER-033): no document denies, a document with
 * no root denies, and an empty ``all_of`` denies. An interface that should
 * forward the full firehose binds ``AllowAllFilter`` in *code* — which makes
 * waving the whole stream through a reviewable decision instead of an empty
 * config box. This module therefore offers no "match all" clause kind and the
 * builder offers no such button; :func:`describeDenyReason` exists to say so out
 * loud rather than leaving the operator to infer it from a blank panel.
 *
 * Cross-references:
 *   - ADR-036 §D-4; REQ-PEER-031 / -032 / -033.
 *   - `packages/meridian_peer_framework/src/meridian_peer_framework/_filter_schema.py`
 *     (the document model this mirrors — a kind added there is added here too).
 */
import { ALERT_PRIORITIES, ALERT_TYPES, MESSAGE_TYPES } from "@/peers/peer-catalog";
import type { ClauseNode, CompositeNode, PeerFilterConfig } from "@/peers/peer-model";
import { FILTER_SCHEMA_VERSION } from "@/peers/peer-model";

/** A clause's position: child indices from the root. ``[]`` addresses the root. */
export type ClausePath = readonly number[];

/** Every leaf selector kind the document model accepts (REQ-PEER-032). */
export const LEAF_CLAUSE_KINDS = [
  "producer",
  "aggregator",
  "message_type",
  "geo",
  "classification",
  "alert_type",
  "alert_priority",
  "minimum_confidence",
] as const;
export type LeafClauseKind = (typeof LEAF_CLAUSE_KINDS)[number];

/** The two composite kinds — structure, not selection. */
export const COMPOSITE_CLAUSE_KINDS = ["all_of", "any_of"] as const;
export type CompositeClauseKind = (typeof COMPOSITE_CLAUSE_KINDS)[number];

/** Operator-facing label per clause kind. */
export const CLAUSE_LABELS: Readonly<Record<LeafClauseKind | CompositeClauseKind, string>> = {
  producer: "Producer (sensor) id",
  aggregator: "Aggregator id",
  message_type: "Message type",
  geo: "Geographic area",
  classification: "Detection classification",
  alert_type: "Alert type",
  alert_priority: "Alert priority",
  minimum_confidence: "Minimum confidence",
  all_of: "All of",
  any_of: "Any of",
};

/** Whether a node is a composite (``all_of`` / ``any_of``). */
export function isComposite(node: ClauseNode): node is CompositeNode {
  return node.kind === "all_of" || node.kind === "any_of";
}

/**
 * A fresh clause of ``kind``, with the model's own defaults.
 *
 * Every set-valued clause starts **empty**, and an empty allow-set matches
 * nothing. That is the fail-safe reading: a clause the operator has added but
 * not filled in narrows the flow to nothing rather than widening it to
 * everything while they think.
 */
export function newClause(kind: LeafClauseKind | CompositeClauseKind): ClauseNode {
  switch (kind) {
    case "producer":
      return { kind: "producer", producer_ids: [] };
    case "aggregator":
      return { kind: "aggregator", aggregator_ids: [] };
    case "message_type":
      return { kind: "message_type", message_types: [] };
    case "geo":
      return {
        kind: "geo",
        source: "detection_location",
        min_latitude_deg: 0,
        max_latitude_deg: 0,
        min_longitude_deg: 0,
        max_longitude_deg: 0,
      };
    case "classification":
      return { kind: "classification", classification_types: [] };
    case "alert_type":
      return { kind: "alert_type", alert_types: [] };
    case "alert_priority":
      return { kind: "alert_priority", priorities: [] };
    case "minimum_confidence":
      return { kind: "minimum_confidence", minimum: 0.5 };
    case "all_of":
      return { kind: "all_of", clauses: [] };
    case "any_of":
      return { kind: "any_of", clauses: [] };
  }
}

/** Read the node at ``path``, or ``null`` when the path does not resolve. */
export function clauseAt(root: ClauseNode, path: ClausePath): ClauseNode | null {
  let node: ClauseNode = root;
  for (const index of path) {
    if (!isComposite(node)) return null;
    const child = node.clauses[index];
    if (child === undefined) return null;
    node = child;
  }
  return node;
}

/** Return a copy of ``root`` with the node at ``path`` replaced by ``next``. */
export function replaceClause(root: ClauseNode, path: ClausePath, next: ClauseNode): ClauseNode {
  if (path.length === 0) return next;
  const [index, ...rest] = path;
  if (!isComposite(root)) return root;
  const child = root.clauses[index];
  if (child === undefined) return root;
  const clauses = [...root.clauses];
  clauses[index] = replaceClause(child, rest, next);
  return { ...root, clauses } as CompositeNode;
}

/**
 * Return a copy of ``root`` with the node at ``path`` removed.
 *
 * Removing the root yields ``null`` — a filter with nothing left denies
 * everything, which is the correct and conservative outcome, not an error.
 */
export function removeClause(root: ClauseNode, path: ClausePath): ClauseNode | null {
  if (path.length === 0) return null;
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  const parent = clauseAt(root, parentPath);
  if (parent === null || !isComposite(parent)) return root;
  const clauses = parent.clauses.filter((_, i) => i !== index);
  return replaceClause(root, parentPath, { ...parent, clauses } as CompositeNode);
}

/** Return a copy of ``root`` with ``child`` appended to the composite at ``path``. */
export function appendClause(root: ClauseNode, path: ClausePath, child: ClauseNode): ClauseNode {
  const target = clauseAt(root, path);
  if (target === null || !isComposite(target)) return root;
  return replaceClause(root, path, {
    ...target,
    clauses: [...target.clauses, child],
  } as CompositeNode);
}

/** Count the leaf selectors beneath ``node`` — composites are structure, not selection. */
export function countLeaves(node: ClauseNode | null): number {
  if (node === null) return 0;
  if (!isComposite(node)) return 1;
  return node.clauses.reduce((total, child) => total + countLeaves(child), 0);
}

/**
 * Canonicalize a root before persisting.
 *
 * A composite with exactly one child means the same thing as that child, and a
 * composite with none means the same thing as no filter at all (deny). Both are
 * collapsed so the stored document matches what the operator actually
 * expressed, and so the ``clause_count`` every role sees in the filter summary
 * does not drift with the editor's scaffolding. Idempotent.
 */
export function canonicalizeClause(node: ClauseNode | null): ClauseNode | null {
  if (node === null) return null;
  if (!isComposite(node)) return node;
  const children = node.clauses
    .map((child) => canonicalizeClause(child))
    .filter((child): child is ClauseNode => child !== null);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return { ...node, clauses: children } as CompositeNode;
}

/**
 * Build the persistable document from an edited root.
 *
 * Returns ``null`` — not an empty document — when nothing survives
 * canonicalization, because ``filter: null`` is exactly how the model spells
 * "this interface emits nothing".
 */
export function toFilterConfig(root: ClauseNode | null): PeerFilterConfig | null {
  const canonical = canonicalizeClause(root);
  if (canonical === null) return null;
  return { schema_version: FILTER_SCHEMA_VERSION, root: canonical, connection_overrides: {} };
}

/**
 * Why this filter currently emits nothing, or ``null`` if it can match
 * something.
 *
 * Surfaced verbatim in the builder. An operator who has added a clause and not
 * filled it in has a filter that denies everything, and the difference between
 * "denies everything" and "not configured yet" is invisible from the outside —
 * an interface in either state is silent. Saying it out loud is the same
 * discipline as never rendering a false green (CLAUDE.md §8.1).
 */
export function describeDenyReason(root: ClauseNode | null): string | null {
  const canonical = canonicalizeClause(root);
  if (canonical === null) {
    return "No filter is defined, so this interface emits nothing. Filters are default-deny: add at least one condition describing what to forward.";
  }
  if (hasEmptyAllowSet(canonical)) {
    return "At least one condition has an empty allow-set, which matches nothing. Fill it in or remove it — an empty condition silences the flow.";
  }
  return null;
}

/**
 * Whether any leaf in the tree carries an allow-set that cannot match.
 *
 * Exported because the fact is direction-neutral while its *reading* is not: on
 * egress an unfillable clause means the interface emits nothing, on ingress it
 * means the interface accepts nothing and the records it declines cease to exist
 * anywhere (:file:`@/providers/provider-filter-tree`). One predicate, two
 * sentences (CLAUDE.md §4.4).
 */
export function hasEmptyAllowSet(node: ClauseNode): boolean {
  if (isComposite(node)) return node.clauses.some(hasEmptyAllowSet);
  switch (node.kind) {
    case "producer":
      return node.producer_ids.length === 0;
    case "aggregator":
      return node.aggregator_ids.length === 0;
    case "message_type":
      return node.message_types.length === 0;
    case "classification":
      return node.classification_types.length === 0;
    case "alert_type":
      return node.alert_types.length === 0;
    case "alert_priority":
      return node.priorities.length === 0;
    default:
      return false;
  }
}

/**
 * A one-line, human summary of a clause — the collapsed row label, and the
 * text a screen reader hears before expanding a condition.
 */
export function describeClause(node: ClauseNode): string {
  switch (node.kind) {
    case "producer":
      return `Producer id in {${node.producer_ids.join(", ")}}`;
    case "aggregator":
      return `Aggregator id in {${node.aggregator_ids.join(", ")}}`;
    case "message_type":
      return `Message type in {${node.message_types.join(", ")}}`;
    case "geo":
      return `${node.source.replace(/_/g, " ")} within ${fmt(node.min_latitude_deg)},${fmt(
        node.min_longitude_deg,
      )} → ${fmt(node.max_latitude_deg)},${fmt(node.max_longitude_deg)}`;
    case "classification":
      return `Classification in {${node.classification_types.join(", ")}}`;
    case "alert_type":
      return `Alert type in {${node.alert_types.join(", ")}}`;
    case "alert_priority":
      return `Alert priority in {${node.priorities.join(", ")}}`;
    case "minimum_confidence":
      return `Confidence ≥ ${node.minimum}`;
    case "all_of":
      return `All of (${node.clauses.length})`;
    case "any_of":
      return `Any of (${node.clauses.length})`;
  }
}

function fmt(value: number): string {
  return Number.isFinite(value) ? value.toFixed(3) : "—";
}

/**
 * Whether a clause kind can ever match on this interface's payload domain.
 *
 * A raw interface carries blobs, so the SAPIENT message-field selectors
 * (classification / alert type / alert priority / confidence) can only ever
 * decline on it — a clause that provably never matches is a silenced flow
 * wearing a filter. The builder greys these out for raw types rather than
 * hiding them, so the constraint is visible rather than mysterious.
 */
export function clauseAppliesTo(
  kind: LeafClauseKind | CompositeClauseKind,
  payloadDomain: "sapient" | "raw",
): boolean {
  if (payloadDomain === "sapient") return true;
  return !["classification", "alert_type", "alert_priority", "minimum_confidence"].includes(kind);
}

/** The closed vocabulary a set-valued leaf draws from, or ``null`` when it is open. */
export function closedVocabulary(kind: LeafClauseKind): readonly string[] | null {
  if (kind === "message_type") return MESSAGE_TYPES;
  if (kind === "alert_type") return ALERT_TYPES;
  if (kind === "alert_priority") return ALERT_PRIORITIES;
  return null;
}
